import { useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Form } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { ApplicationRejectionModal, type ApplicationRejectionForm } from './application-rejection-modal.tsx'
import { ApplicationSummary, ProcessedApplicationDetails } from './application-review-details.tsx'
import { ApplicationReviewFields } from './application-review-fields.tsx'
import {
  getApplicationReviewInitialValues,
  toApplicationHireDate,
  type ApplicationReviewFormValues
} from './application-review-form.ts'

export type ApplicationReviewRecord = {
  id: string
  applicantName: string
  applicantPhone: string | null
  status: 'pending' | 'approved' | 'rejected'
  assignedRoleCodes: string[]
  rejectionReason: string | null
  createdAt: Date
  decidedAt: Date | null
  account: { email: string }
  member: { email: string | null; jobTitle: string | null; name: string } | null
  decidedBy: { email: string; member: { name: string } | null } | null
}

type ReviewForm = ApplicationReviewFormValues

export function ApplicationReviewModal({
  application,
  onClose,
  onHandled
}: {
  application?: ApplicationReviewRecord
  onClose: () => void
  onHandled: () => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<ReviewForm>()
  const [rejectForm] = Form.useForm<ApplicationRejectionForm>()
  const [rejectOpen, setRejectOpen] = useState(false)
  const memberMode = Form.useWatch('memberMode', form)
  const employees = useQuery({
    ...trpc.accountApplication.unboundEmployeeOptions.queryOptions({}),
    enabled: application?.status === 'pending'
  })
  const approve = useMutation(trpc.accountApplication.approve.mutationOptions())
  const reject = useMutation(trpc.accountApplication.reject.mutationOptions())
  const initialValues = getApplicationReviewInitialValues(application)

  const handleApprove = async () => {
    if (!application) {
      return
    }
    try {
      const fields =
        memberMode === 'create'
          ? ['memberMode', 'type', 'name', 'jobTitle', 'email', 'phone', 'isSalesperson', 'hireDate', 'note', 'roleIds']
          : ['memberMode', 'memberId', 'roleIds']
      const values = await form.validateFields(fields)
      await approve.mutateAsync({
        applicationId: application.id,
        member:
          values.memberMode === 'existing'
            ? { mode: 'existing', memberId: values.memberId! }
            : {
                mode: 'create',
                name: values.name!,
                phone: values.phone,
                jobTitle: values.jobTitle!,
                isSalesperson: values.isSalesperson,
                hireDate: toApplicationHireDate(values.hireDate),
                note: values.note
              },
        roleIds: values.roleIds
      })
      void message.success('账号申请已批准')
      await onHandled()
    } catch (error) {
      if (typeof error === 'object' && error && 'errorFields' in error) {
        const firstField = (
          error as {
            errorFields?: Array<{ name: Array<string | number> }>
          }
        ).errorFields?.[0]?.name
        if (firstField) {
          form.scrollToField(firstField, { focus: true })
        }
        return
      }
      void message.error(getErrorMessage(error, '批准失败'))
    }
  }

  const handleReject = async (values: ApplicationRejectionForm) => {
    if (!application) {
      return false
    }
    try {
      await reject.mutateAsync({
        applicationId: application.id,
        reason: values.rejectionReason.trim()
      })
      void message.success('账号申请已拒绝')
      setRejectOpen(false)
      await onHandled()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '拒绝失败'))
      return false
    }
  }

  return (
    <>
      <ModalForm<ReviewForm>
        form={form}
        initialValues={initialValues}
        layout="vertical"
        modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
        onOpenChange={(open) => {
          if (!open) {
            onClose()
          }
        }}
        open={Boolean(application)}
        scrollToFirstError={{ focus: true }}
        submitter={{
          render: () =>
            application?.status === 'pending'
              ? [
                  <Button
                    disabled={reject.isPending}
                    key="approve"
                    loading={approve.isPending}
                    onClick={() => void handleApprove()}
                    type="primary"
                  >
                    批准
                  </Button>,
                  <Button
                    danger
                    disabled={approve.isPending}
                    key="reject"
                    loading={reject.isPending}
                    onClick={() => {
                      rejectForm.resetFields()
                      setRejectOpen(true)
                    }}
                  >
                    拒绝
                  </Button>,
                  <Button disabled={approve.isPending || reject.isPending} key="cancel" onClick={onClose}>
                    取消
                  </Button>
                ]
              : [
                  <Button key="close" onClick={onClose}>
                    关闭
                  </Button>
                ]
        }}
        title={`${application?.status === 'pending' ? '审核' : '申请详情'} · ${application?.applicantName ?? ''}`}
        width={680}
      >
        {application ? (
          <>
            <ApplicationSummary application={application} />
            {application.status === 'pending' ? (
              <ApplicationReviewFields
                approvePending={approve.isPending}
                employees={employees.data}
                employeesLoading={employees.isLoading}
                memberMode={memberMode}
                requiresSalesperson={false}
              />
            ) : (
              <ProcessedApplicationDetails application={application} />
            )}
          </>
        ) : null}
      </ModalForm>

      <ApplicationRejectionModal
        applicantName={application?.applicantName}
        form={rejectForm}
        onFinish={handleReject}
        onClose={() => setRejectOpen(false)}
        open={rejectOpen}
        pending={reject.isPending}
      />
    </>
  )
}
